import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { LOGO_URL } from "../lib/api";

const SUBMIT_LABELS = {
  login: { idle: "Sign in", busy: "Signing in..." },
  register: { idle: "Create account", busy: "Creating..." },
};

export default function AuthModal() {
  const { modalOpen, closeAuth, login, register, onAuthSuccess } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Reset form whenever modal opens/closes
  useEffect(() => {
    if (!modalOpen) {
      setError("");
      setBusy(false);
    }
  }, [modalOpen]);

  // Prevent background scroll while open
  useEffect(() => {
    if (modalOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [modalOpen]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const fn = mode === "login" ? login : register;
    const args = mode === "login" ? [email.trim(), password] : [email.trim(), password, name.trim()];
    const res = await fn(...args);
    if (res.ok) {
      onAuthSuccess();
    } else {
      setError(res.error || "Something went wrong");
    }
    setBusy(false);
  };

  const toggleMode = () => {
    setMode((m) => (m === "login" ? "register" : "login"));
    setError("");
  };

  return (
    <AnimatePresence>
      {modalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/80 backdrop-blur-md px-3 md:px-6"
          data-testid="auth-modal"
          onClick={closeAuth}
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative w-full max-w-md bg-[var(--hk-surface)] border border-white/10 shadow-[0_0_60px_rgba(147,51,234,0.25)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-[var(--hk-orange)] to-transparent" />
            <button
              data-testid="auth-modal-close"
              onClick={closeAuth}
              className="absolute top-4 right-4 p-2 text-white/70 hover:text-white"
              aria-label="Close"
            >
              <X size={22} />
            </button>

            <div className="px-6 pt-10 pb-3 text-center">
              <img src={LOGO_URL} alt="" className="w-16 h-16 mx-auto mb-4 drop-shadow-[0_0_25px_rgba(147,51,234,0.7)]" />
              <p className="font-mono-alt text-[10px] tracking-[0.4em] text-[var(--hk-orange)] mb-2">
                / {mode === "login" ? "WELCOME BACK" : "JOIN THE LIST"}
              </p>
              <h2 className="font-display text-3xl font-bold uppercase tracking-tighter text-white">
                {mode === "login" ? "Sign in" : "Create account"}
              </h2>
              <p className="text-xs text-gray-400 mt-2">
                Cash on delivery requires an account so we can confirm your drop.
              </p>
            </div>

            <form onSubmit={submit} className="px-6 pb-8 pt-3 space-y-4" data-testid="auth-form">
              {mode === "register" && (
                <Field
                  label="Name"
                  icon={<UserIcon size={16} />}
                  type="text"
                  value={name}
                  onChange={setName}
                  testid="auth-name"
                  autoComplete="name"
                />
              )}
              <Field
                label="Email"
                icon={<Mail size={16} />}
                type="email"
                value={email}
                onChange={setEmail}
                required
                testid="auth-email"
                autoComplete="email"
                inputMode="email"
              />
              <Field
                label="Password"
                icon={<Lock size={16} />}
                type="password"
                value={password}
                onChange={setPassword}
                required
                minLength={6}
                testid="auth-password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
              />

              {error && (
                <p
                  data-testid="auth-error"
                  className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-sm"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={busy || !email || !password}
                data-testid="auth-submit"
                className="w-full bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider py-3.5 rounded-sm hover:bg-[var(--hk-orange-hover)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                {SUBMIT_LABELS[mode][busy ? "busy" : "idle"]}
              </button>

              <div className="pt-1 text-center">
                <button
                  type="button"
                  onClick={toggleMode}
                  data-testid="auth-toggle-mode"
                  className="text-xs uppercase tracking-widest text-gray-400 hover:text-[var(--hk-orange)]"
                >
                  {mode === "login"
                    ? "No account yet? Create one"
                    : "Already a customer? Sign in"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, icon, type, value, onChange, required, minLength, testid, autoComplete, inputMode }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-2">
        {label}{required ? " *" : ""}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{icon}</span>
        <input
          data-testid={testid}
          type={type}
          required={required}
          minLength={minLength}
          autoComplete={autoComplete}
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black/40 border border-white/15 focus:border-[var(--hk-purple)] outline-none rounded-sm pl-10 pr-3 py-3 text-base text-white placeholder-gray-600"
        />
      </div>
    </div>
  );
}
